import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { makeStyles, Grid, Typography } from '@material-ui/core'
import Filters from 'components/FilterList/Filters'
import { Form } from 'react-final-form'
import Button from 'components/Button'
import Accordion from 'components/Accordion'
import { FieldText } from 'components/Fields'
import { useSelector, useDispatch } from 'react-redux'
import { getTiposGases, getIsSavingTiposGases, getIsUpdatingTiposGases } from 'store/modules/tiposGases/tiposGases.selectors'
import { validateFormValues } from 'utils/forms'
import { useConfirmationDialog } from 'components/Dialog/ConfirmDialog'
import { callCriarTipoGas, callExcluirTipoGas, callAtualizarTipoGas } from 'store/modules/tiposGases'
import { getTipoUsuarioLogado } from 'store/modules/auth/auth.selectors'
import { TIPO_USUARIO } from 'utils/constantes'

const useStyles = makeStyles((theme) => ({
  sectionTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey.main}`,
  },
  screenTitle: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(5),
    fontSize: theme.typography.pxToRem(24),
  },
  search: {
    display: 'flex',
    justifyContent: 'space-between',
    width: (props) => props.fullWidth && '100%',
  },
  hidden: {
    display: 'none',
  },
  formAdd: {
    transition: '0.20s ease-out',
    maxHeight: 0,
    overflow: 'hidden',
  },
  openFormAdd: {
    paddingTop: 10,
    marginTop: '22px',
    maxHeight: 300,
    transition: '0.25s ease-in',
  },
  normalTop: {
    marginTop: '16px',
  },
  listItems: {
    marginTop: '32px',
    border: '1px solid #C9CEDB',
    boxShadow: '0px 8px 32px rgba(15, 52, 68, 0.08)',
    borderRadius: '8px',
    padding: '0px 16px',
  },
  itemTitle: {
    fontFamily: 'Poppins',
    color: '#0F3444',
    fontWeight: '600',
    fontSize: '16px',
  },
  divider: {
    height: 1,
    width: 'calc(100% + 32px)',
    marginLeft: '-16px',
    backgroundColor: theme.palette.grey.main,
  },
}))

const Gee = ({ paisId }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const dispatch = useDispatch()

  const isSaving = useSelector(getIsSavingTiposGases)
  const isUpdating = useSelector(getIsUpdatingTiposGases)
  const tipoUsuarioLogado = useSelector(getTipoUsuarioLogado)

  const desabilitarFormulario = Boolean(tipoUsuarioLogado === TIPO_USUARIO.ADMIN.OBSERVADOR)

  const filterScreen = [
    { value: true, label: 'filtros.nomeAZ', name: 'order' },
    { value: false, label: 'filtros.nomeZA', name: 'order' },
  ]
  const dialog = useConfirmationDialog()
  const listaTiposGases = useSelector(getTiposGases)
  const [data, setData] = useState(listaTiposGases)

  const [controlAdded, setControlAdded] = useState(false)

  const validation = yup.object({
    gas: yup.string().required(),
    formula: yup.string().required(),
    pag100: yup.string().required(),
  })

  const handleUpdate = (values) => {
    const { id } = values
    const payload = ({ ...values, paisId })
    delete payload.id
    dispatch(callAtualizarTipoGas({ id, tiposGas: payload }))
  }

  const handleSave = (values, form) => {
    const payload = ({ ...values, paisId })
    dispatch(callCriarTipoGas(payload))
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  const handleDelete = (id, gas) => dialog.showConfirmation({
    title: t('dialogs.confirmarExclusao'),
    message: t('dialogs.excluirGas', {
      gas,
    }),
  }).then((result) => {
    if (result) {
      dispatch(callExcluirTipoGas({ id }))
    }
    return null
  })

  const [openItem, setOpenItem] = useState('')

  const handleExpand = (item) => {
    setOpenItem(openItem === item ? '' : item)
  }

  const ItemForm = (position) => {
    const itemGas = [data[position]]
    return (
      <Form
        onSubmit={handleUpdate}
        validate={validateFormValues(validation)}
        initialValues={{
          gas: itemGas[0]?.gas ?? '',
          formula: itemGas[0]?.formula ?? '',
          pag100: itemGas[0]?.pag100 ?? '',
          id: itemGas[0]?.id ?? '',
        }}
        render={({ handleSubmit, form, pristine, valid }) => (
          <form onSubmit={handleSubmit}>
            <Accordion
              hideButton
              variant="no-borders"
              classes={{ title: classes.itemTitle }}
              title={itemGas[0]?.gas}
              expanded={position === openItem}
              onExpand={() => handleExpand(position)}
              onDelete={() => {
                handleDelete(itemGas[0]?.id, itemGas[0]?.gas)
                handleExpand('')
              }}
              disableDelete={desabilitarFormulario}
            >
              <Grid container>
                <Grid item xs={12}>
                  <FieldText
                    required
                    label={t('inputs.gas')}
                    name="gas"
                    disabled={desabilitarFormulario}
                  />
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={9} className={classes.normalTop}>
                    <FieldText
                      required
                      label={t('inputs.Formula')}
                      name="formula"
                      disabled={desabilitarFormulario}
                    />
                  </Grid>
                  <Grid item xs={3} className={classes.normalTop}>
                    <FieldText
                      required
                      type="number"
                      label={t('inputs.Pag')}
                      name="pag100"
                      disabled={desabilitarFormulario}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={() => { form.reset(); handleExpand('') }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={Boolean(pristine || !valid) || desabilitarFormulario}
                        loading={isUpdating}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Accordion>
            <div className={classes.divider} />
          </form>
        )}
      />
    )
  }

  const clearFields = (form, values, type) => {
    if (type === 'add') {
      setControlAdded(!controlAdded)
    }
    form.reset()
    Object.keys(values).forEach((key) => {
      form.resetFieldState(key)
    })
  }

  useEffect(() => {
    setData(listaTiposGases)
  }, [listaTiposGases])

  return (
    <>
      <div className={classes.sectionTitle}>
        <Typography className={classes.screenTitle}>
          {t('paisesCalculos.TabelaPAGGases')}
        </Typography>
        <Button
          variant="default"
          disabled={controlAdded || desabilitarFormulario}
          onClick={() => {
            setControlAdded(true)
            handleExpand('')
          }}
        >
          {t('paisesCalculos.AdicionarGas')}
        </Button>
      </div>
      <div className={clsx(classes.search, controlAdded && classes.hidden)}>
        <Filters
          fullWidth
          items={listaTiposGases}
          setItems={setData}
          origin={{ vertical: 'top', horizontal: 'right' }}
          filterScreen={filterScreen}
          nameSearch="gas"
        />
      </div>
      <div
        className={clsx(
          classes.formAdd,
          controlAdded && classes.openFormAdd,
        )}
      >
        <Form
          onSubmit={handleSave}
          initialValues={{
            gas: '',
            formula: '',
            pag100: '',
          }}
          validate={validateFormValues(validation)}
          render={({ handleSubmit, form, values, pristine, valid }) => (
            <form onSubmit={handleSubmit}>
              <Grid container>
                <Grid item xs={12}>
                  <FieldText
                    required
                    label={t('inputs.gas')}
                    name="gas"
                  />
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={9} className={classes.normalTop}>
                    <FieldText
                      required
                      label={t('inputs.Formula')}
                      name="formula"
                    />
                  </Grid>
                  <Grid item xs={3} className={classes.normalTop}>
                    <FieldText
                      required
                      type="number"
                      label={t('inputs.Pag')}
                      name="pag100"
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    className={classes.normalTop}
                  >
                    <Grid item className={classes.deleteButton}>
                      <Button
                        variant="danger"
                        onClick={(e) => {
                          e.preventDefault()
                          clearFields(form, values, 'add')
                        }}
                      >
                        {t('inputs.cancelar')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        disabled={pristine || !valid}
                        loading={isSaving}
                      >
                        {t('inputs.salvar')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          )}
        />

      </div>
      <div className={classes.listItems}>
        {
          data.length > 0 &&
          data.map((elem, index) => (
            <Fragment key={index}>
              {ItemForm(index)}
            </Fragment>
          ))
      }
      </div>
    </>
  )
}

Gee.propTypes = {
  paisId: PropTypes.number,
}

export default Gee
